import React from 'react'
import { Box, Typography, Grid } from '@mui/material'

interface NicheStatsData {
  totalContent: number
  withSummary: number
  withoutSummary: number
  byPlatform: Array<{ platform: string; count: number }>
}

interface NicheStatsProps {
  stats: NicheStatsData | null
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string
  value: number | string
  color?: string
}): JSX.Element {
  return (
    <Box
      sx={{
        background: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(168, 85, 247, 0.2)',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: 700,
          color: color ?? '#a855f7',
          lineHeight: 1.2,
        }}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: 'rgba(255,255,255,0.5)',
          marginTop: '4px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

export default function NicheStats({ stats }: NicheStatsProps): JSX.Element {
  if (!stats) {
    return (
      <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
        Loading niche stats...
      </Typography>
    )
  }

  const summaryRate =
    stats.totalContent > 0
      ? Math.round((stats.withSummary / stats.totalContent) * 100)
      : 0

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 600,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '16px',
        }}
      >
        Niche Scraping Stats
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Content" value={stats.totalContent} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="With Summary" value={stats.withSummary} color="#22c55e" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Without Summary" value={stats.withoutSummary} color="#ef4444" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Summary Rate" value={`${summaryRate}%`} color="#3b82f6" />
        </Grid>
      </Grid>
      {stats.byPlatform.length > 0 && (
        <Box sx={{ marginTop: '16px' }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.5)',
              marginBottom: '8px',
              fontWeight: 600,
            }}
          >
            By Platform
          </Typography>
          <Grid container spacing={2}>
            {stats.byPlatform.map(p => (
              <Grid item xs={6} md={2} key={p.platform}>
                <StatCard label={p.platform} value={p.count} color="#8b5cf6" />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  )
}
